import { incidentCanonicalFollowupOwnerLabel } from '../../components/incidents/reviewCanonical';
import { ownerRoleIsUnassigned } from './incidentPatternVocabulary';

export type GroupedCorrectiveWorkflowPhase =
  | 'locked'
  | 'current'
  | 'planned'
  | 'started'
  | 'completed'
  | 'validated';

export type GroupedCorrectivePlanStatusLabel =
  | 'Locked'
  | 'Draft'
  | 'Planned'
  | 'Started'
  | 'Completed'
  | 'Validated';

type WorkflowCorrectiveActionLike = {
  action_type?: string | null;
  priority?: string | null;
  verification_method?: string | null;
  assignee?: string | null;
  status?: string | null;
  due_date?: string | null;
};

type WorkflowCorrectiveActionCollection = {
  active_actions?: WorkflowCorrectiveActionLike[] | null;
  completed_actions?: WorkflowCorrectiveActionLike[] | null;
};

export function groupedCorrectiveWorkflowPhaseLabel(phase: GroupedCorrectiveWorkflowPhase): string {
  switch (phase) {
    case 'locked':
      return 'Locked';
    case 'current':
      return 'Current';
    case 'planned':
      return 'Planned';
    case 'started':
      return 'Started';
    case 'completed':
      return 'Completed';
    case 'validated':
      return 'Validated';
    default:
      return 'Pending';
  }
}

function normalizeWorkflowActionStatus(status: string | null | undefined): string {
  return String(status || '').trim().toLowerCase();
}

function primaryWorkflowCorrectiveAction(
  workflowCorrectiveActions: WorkflowCorrectiveActionCollection | null | undefined,
): WorkflowCorrectiveActionLike | null {
  const workflowActionState = workflowCorrectiveActions || {};
  const active = Array.isArray(workflowActionState.active_actions)
    ? workflowActionState.active_actions
    : [];
  const completed = Array.isArray(workflowActionState.completed_actions)
    ? workflowActionState.completed_actions
    : [];
  return active[0] || completed[0] || null;
}

export function readPersistedCorrectiveAction(
  incident: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const safetyCase = (incident as {
    safety_case?: { corrective_action?: Record<string, unknown> };
  })?.safety_case;
  if (safetyCase?.corrective_action && typeof safetyCase.corrective_action === 'object') {
    return safetyCase.corrective_action as Record<string, unknown>;
  }

  const envelopeMetadata = (incident as {
    envelope_metadata?: { corrective_action?: Record<string, unknown> };
    raw?: { envelope_metadata?: { corrective_action?: Record<string, unknown> } };
  })?.envelope_metadata;
  if (envelopeMetadata?.corrective_action && typeof envelopeMetadata.corrective_action === 'object') {
    return envelopeMetadata.corrective_action as Record<string, unknown>;
  }

  const rawEnvelopeMetadata = (incident as {
    raw?: { envelope_metadata?: { corrective_action?: Record<string, unknown> } };
  })?.raw?.envelope_metadata;
  if (
    rawEnvelopeMetadata?.corrective_action
    && typeof rawEnvelopeMetadata.corrective_action === 'object'
  ) {
    return rawEnvelopeMetadata.corrective_action as Record<string, unknown>;
  }

  return {};
}

function readPersistedNextReviewDate(incident: Record<string, unknown> | null | undefined): string {
  const corrective = readPersistedCorrectiveAction(incident);
  return String(
    corrective.due_at
    || corrective.due_date
    || (incident as { sla_target_at?: string })?.sla_target_at
    || (incident as { followup_next_review_at?: string })?.followup_next_review_at
    || '',
  ).trim();
}

function normalizeLocalDateInputValue(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw.includes('T') ? raw.slice(0, 10) : raw;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

export function isFollowUpStatusClosed(opsState: string | null | undefined): boolean {
  return String(opsState || '').trim().toLowerCase() === 'closed';
}

export function isFollowUpStatusInProgress(opsState: string | null | undefined): boolean {
  const state = String(opsState || '').trim().toLowerCase();
  return state === 'in_progress' || state === 'acknowledged' || state === 'resolved';
}

export function hasGroupedCorrectivePlanFields(args: {
  followUpOwnerLabel?: string | null;
  actionType?: string | null;
  priority?: string | null;
  nextReviewDate?: string | null;
}): boolean {
  if (ownerRoleIsUnassigned(args.followUpOwnerLabel)) return false;
  return Boolean(
    String(args.actionType || '').trim()
    && String(args.priority || '').trim()
    && String(args.nextReviewDate || '').trim(),
  );
}

/** Whether the grouped "Create action plan" control should be interactive. */
export function groupedCorrectiveActionSaveEnabled(args: {
  incidentId?: string | null;
  workflowIdAvailable?: boolean;
  drawerBusy?: string | null;
  canUseOpsAutomation?: boolean;
  canManageIncidentWorkflow?: boolean;
  planSaved?: boolean;
  planDraftDirty?: boolean;
  followUpOwnerLabel?: string | null;
  pendingOwnerDraft?: string | null;
  actionType?: string | null;
  priority?: string | null;
  nextReviewDate?: string | null;
}): boolean {
  if (!String(args.incidentId || '').trim()) return false;
  if (args.workflowIdAvailable === false) return false;
  if (!args.canUseOpsAutomation && !args.canManageIncidentWorkflow) return false;
  if (args.drawerBusy === 'ops-corrective') return false;
  if (args.planSaved && !args.planDraftDirty) return false;
  const owner = String(args.pendingOwnerDraft || args.followUpOwnerLabel || '').trim();
  return hasGroupedCorrectivePlanFields({
    followUpOwnerLabel: owner,
    actionType: args.actionType,
    priority: args.priority,
    nextReviewDate: args.nextReviewDate,
  });
}

export function groupedCorrectiveActionSaveDisabledReason(args: {
  incidentId?: string | null;
  workflowIdAvailable?: boolean;
  drawerBusy?: string | null;
  canUseOpsAutomation?: boolean;
  canManageIncidentWorkflow?: boolean;
  planSaved?: boolean;
  planDraftDirty?: boolean;
  followUpOwnerLabel?: string | null;
  pendingOwnerDraft?: string | null;
  actionType?: string | null;
  priority?: string | null;
  nextReviewDate?: string | null;
}): string | null {
  if (groupedCorrectiveActionSaveEnabled(args)) return null;
  if (args.planSaved && !args.planDraftDirty) {
    return 'Action plan saved. Change type, priority, or next review date to update.';
  }
  if (!String(args.incidentId || '').trim()) {
    return 'Select representative evidence before saving the action plan.';
  }
  if (args.workflowIdAvailable === false) {
    return 'This grouped review is missing workflow context. Reopen it from the recurring risk workflow before saving the action plan.';
  }
  if (!args.canUseOpsAutomation && !args.canManageIncidentWorkflow) {
    return 'You need incident write access to save corrective actions.';
  }
  if (args.drawerBusy === 'ops-corrective') {
    return 'Saving the corrective action plan…';
  }
  const owner = String(args.pendingOwnerDraft || args.followUpOwnerLabel || '').trim();
  if (ownerRoleIsUnassigned(owner)) {
    return 'Enter and save an action owner in Step 1, or fill the owner field here before creating the plan.';
  }
  if (!String(args.actionType || '').trim() || !String(args.priority || '').trim() || !String(args.nextReviewDate || '').trim()) {
    return 'Choose corrective action type, priority, and next review date to enable Create action plan.';
  }
  return null;
}

export function isGroupedCorrectiveActionPlanSaved(
  incident: Record<string, unknown> | null | undefined,
): boolean {
  if (!incident) return false;
  const corrective = readPersistedCorrectiveAction(incident);
  return hasGroupedCorrectivePlanFields({
    followUpOwnerLabel: incidentCanonicalFollowupOwnerLabel(incident),
    actionType: String(corrective.action_type || ''),
    priority: String(corrective.priority || ''),
    nextReviewDate: readPersistedNextReviewDate(incident),
  });
}

export function isGroupedCorrectiveActionPlanDraftDirty(args: {
  incident: Record<string, unknown> | null | undefined;
  followUpOwnerLabel?: string | null;
  actionType?: string | null;
  priority?: string | null;
  verificationMethod?: string | null;
  nextReviewDate?: string | null;
}): boolean {
  if (!isGroupedCorrectiveActionPlanSaved(args.incident)) return true;
  const corrective = readPersistedCorrectiveAction(args.incident);
  const persistedOwner = incidentCanonicalFollowupOwnerLabel(args.incident);
  const persistedType = String(corrective.action_type || '').trim().toLowerCase();
  const persistedPriority = String(corrective.priority || '').trim().toLowerCase();
  const persistedVerificationMethod = String(corrective.verification_method || '').trim().toLowerCase();
  const persistedReview = readPersistedNextReviewDate(args.incident);
  const normalizedPersistedReview = normalizeLocalDateInputValue(persistedReview);
  const normalizedDraftReview = normalizeLocalDateInputValue(String(args.nextReviewDate || '').trim());
  return (
    String(args.followUpOwnerLabel || '').trim() !== String(persistedOwner || '').trim()
    || String(args.actionType || '').trim().toLowerCase() !== persistedType
    || String(args.priority || '').trim().toLowerCase() !== persistedPriority
    || String(args.verificationMethod || '').trim().toLowerCase() !== persistedVerificationMethod
    || normalizedDraftReview !== normalizedPersistedReview
  );
}

export function resolveGroupedCorrectiveWorkflowPhase(args: {
  evidenceConfirmed?: boolean;
  planSaved?: boolean;
  followUpOwnerLabel?: string | null;
  opsState?: string | null;
}): GroupedCorrectiveWorkflowPhase {
  if (!args.evidenceConfirmed) return 'locked';
  if (!args.planSaved) return 'current';
  return 'planned';
}

/** Step 2 plan card status — not follow-up execution (Step 3 uses ops_state separately). */
export function deriveGroupedCorrectivePlanStatusLabel(args: {
  correctiveActionLocked?: boolean;
  planSaved?: boolean;
  planDraftDirty?: boolean;
  followUpOwnerLabel?: string | null;
  /** @deprecated Ignored for plan status; follow-up state belongs in Step 3. */
  opsState?: string | null;
}): GroupedCorrectivePlanStatusLabel {
  if (args.correctiveActionLocked) return 'Locked';
  if (args.planSaved && !args.planDraftDirty) return 'Planned';
  if (ownerRoleIsUnassigned(args.followUpOwnerLabel) || !args.planSaved || args.planDraftDirty) {
    return 'Draft';
  }
  return 'Planned';
}

/** Policy / rules engine labels are not human action owners. */
export function isAutomatedRiskSourceLabel(label: string | null | undefined): boolean {
  const raw = String(label || '').trim();
  if (!raw) return false;
  if (/@/.test(raw)) return false;
  return /rules|policy|detection|edge safety|system/i.test(raw);
}

export function groupedRiskSourceFieldLabel(label: string | null | undefined): string {
  return isAutomatedRiskSourceLabel(label) ? 'Risk source' : 'Risk owner';
}

export function deriveGroupedCorrectiveWorkflowState(args: {
  incident: Record<string, unknown> | null | undefined;
  evidenceConfirmed: boolean;
  followUpOwnerLabel?: string | null;
  actionType?: string | null;
  priority?: string | null;
  verificationMethod?: string | null;
  nextReviewDate?: string | null;
  opsStateDraft?: string | null;
  workflowCorrectiveActions?: WorkflowCorrectiveActionCollection | null;
}): {
  planSaved: boolean;
  planDraftDirty: boolean;
  phase: GroupedCorrectiveWorkflowPhase;
  complete: boolean;
} {
  const incident = args.incident;
  const persistedOpsState = String(
    (incident as { safety_case?: { corrective_action?: { action_state?: string } }; ops_state?: string })
      ?.safety_case?.corrective_action?.action_state
    || (incident as { ops_state?: string })?.ops_state
    || '',
  ).trim();
  const planSaved = incident ? isGroupedCorrectiveActionPlanSaved(incident) : false;
  const legacyPlanDraftDirty = incident
    ? isGroupedCorrectiveActionPlanDraftDirty({
      incident,
      followUpOwnerLabel: args.followUpOwnerLabel,
      actionType: args.actionType,
      priority: args.priority,
      verificationMethod: args.verificationMethod,
      nextReviewDate: args.nextReviewDate,
    })
    : true;
  const workflowPrimaryAction = primaryWorkflowCorrectiveAction(args.workflowCorrectiveActions);
  const workflowActionState = args.workflowCorrectiveActions || {};
  const workflowActionRows = [
    ...(Array.isArray(workflowActionState.active_actions)
      ? workflowActionState.active_actions
      : []),
    ...(Array.isArray(workflowActionState.completed_actions)
      ? workflowActionState.completed_actions
      : []),
  ];
  const workflowPlanSaved = workflowActionRows.length > 0;
  const normalizedWorkflowReview = normalizeLocalDateInputValue(
    String(workflowPrimaryAction?.due_date || '').trim(),
  );
  const normalizedDraftReview = normalizeLocalDateInputValue(String(args.nextReviewDate || '').trim());
  const workflowPlanDraftDirty = workflowPrimaryAction
    ? (
      String(args.followUpOwnerLabel || '').trim() !== String(workflowPrimaryAction.assignee || '').trim()
      || String(args.actionType || '').trim().toLowerCase()
        !== String(workflowPrimaryAction.action_type || '').trim().toLowerCase()
      || String(args.priority || '').trim().toLowerCase()
        !== String(workflowPrimaryAction.priority || '').trim().toLowerCase()
      || String(args.verificationMethod || '').trim().toLowerCase()
        !== String(workflowPrimaryAction.verification_method || '').trim().toLowerCase()
      || normalizedDraftReview !== normalizedWorkflowReview
    )
    : true;
  const anyWorkflowActionStarted = workflowActionRows.some(
    (action) => normalizeWorkflowActionStatus(action?.status) === 'started',
  );
  const anyWorkflowActionValidated = workflowActionRows.some(
    (action) => normalizeWorkflowActionStatus(action?.status) === 'validated',
  );
  const anyWorkflowActionComplete = workflowActionRows.some((action) =>
    ['completed', 'validated'].includes(normalizeWorkflowActionStatus(action?.status)),
  );
  const effectivePlanSaved = workflowPlanSaved || planSaved;
  const planDraftDirty = workflowPlanSaved ? workflowPlanDraftDirty : legacyPlanDraftDirty;
  const phase = resolveGroupedCorrectiveWorkflowPhase({
    evidenceConfirmed: args.evidenceConfirmed,
    planSaved: effectivePlanSaved,
    followUpOwnerLabel: args.followUpOwnerLabel,
    opsState: args.opsStateDraft || persistedOpsState,
  });
  const effectivePhase = !args.evidenceConfirmed
    ? 'locked'
    : anyWorkflowActionValidated
      ? 'validated'
      : anyWorkflowActionComplete
        ? 'completed'
        : workflowPlanSaved && anyWorkflowActionStarted
          ? 'started'
        : workflowPlanSaved
          ? 'planned'
          : phase;
  return {
    planSaved: effectivePlanSaved,
    planDraftDirty,
    phase: effectivePhase,
    complete: effectivePhase === 'completed' || effectivePhase === 'validated',
  };
}
